import React, { useState, useEffect, useRef } from "react";
import AiResult from "./AiResult";
import { getAuthHeaders } from "../utils/auth";
import PromptTemplateModal, { loadTemplates } from "./PromptTemplateModal";
import { useLanguage } from "../utils/i18n";

export default function ExplainTab({ book, api, showToast, onAddNote, result, onSetResult }) {
  const [concept, setConcept] = useState("");
  const [loading, setLoading] = useState(null); // null | "normal" | "simple"
  const [followUps, setFollowUps] = useState([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [followUpLoading, setFollowUpLoading] = useState(false);
  // concept-level attachments: [{id, name, type:'image'|'doc', base64, text, preview, loading, error}]
  const [conceptAttachments, setConceptAttachments] = useState([]);
  // follow-up attachments
  const [followAttachments, setFollowAttachments] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState(() => loadTemplates());
  const followUpRef = useRef(null);
  const conceptFileRef = useRef(null);
  const followFileRef = useRef(null);
  const { t } = useLanguage();

  const readAsAttachment = (file, setFn) => {
    const id = `${Date.now()}-${Math.random()}`;
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setFn((prev) => [...prev, { id, name: file.name, type: "image", base64, preview: reader.result, loading: false }]);
      };
      reader.readAsDataURL(file);
    } else {
      setFn((prev) => [...prev, { id, name: file.name, type: "doc", loading: true }]);
      (async () => {
        try {
          const authHeaders = await getAuthHeaders();
          const formData = new FormData();
          formData.append("file", file);
          const res = await fetch(`${api}/extract-text`, { method: "POST", headers: authHeaders, body: formData });
          const data = await res.json();
          if (data.error) throw new Error(data.error);
          setFn((prev) => prev.map((a) => (a.id === id ? { ...a, loading: false, text: data.text } : a)));
        } catch (err) {
          setFn((prev) => prev.map((a) => (a.id === id ? { ...a, loading: false, error: err.message || "Failed" } : a)));
        }
      })();
    }
  };

  // Reset follow-ups when a new explanation is generated
  const prevResult = useRef(result);
  useEffect(() => {
    if (result && result !== prevResult.current) setFollowUps([]);
    prevResult.current = result;
  }, [result]);

  const doFetch = async (endpoint, label) => {
    const readyConcept = conceptAttachments.filter((a) => !a.loading && !a.error);
    if (!concept.trim() && readyConcept.length === 0) { showToast(t("enterConceptFirst")); return; }
    setLoading(label);
    setFollowUps([]);
    const sentAttachments = [...readyConcept];
    setConceptAttachments([]);
    const templateKey = label === "simple" ? "explainSimple" : "explain";
    const customTemplate = savedTemplates[templateKey] || null;
    const imageAttachments = sentAttachments.filter((a) => a.type === "image");
    const docAttachments = sentAttachments.filter((a) => a.type === "doc" && a.text);
    const doc_context = docAttachments.map((a) => `[${a.name}]\n${a.text}`).join("\n\n---\n\n");
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${api}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          book_id: book?.id || null,
          concept,
          images: imageAttachments.map((a) => a.base64),
          doc_context: doc_context || null,
          custom_prompt_template: customTemplate,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        onSetResult(data.explanation);
      } else {
        showToast(data.error || t("explainFailed"));
      }
    } catch {
      showToast(t("failExplain"));
    }
    setLoading(null);
  };

  const sendFollowUp = async () => {
    const q = followUpInput.trim();
    const readyFollow = followAttachments.filter((a) => !a.loading && !a.error);
    if (!q && readyFollow.length === 0) return;
    if (!result) return;
    setFollowUpInput("");
    const sentFollow = [...readyFollow];
    setFollowAttachments([]);
    setFollowUpLoading(true);
    const imageAttachments = sentFollow.filter((a) => a.type === "image");
    const docAttachments = sentFollow.filter((a) => a.type === "doc" && a.text);
    const doc_context = docAttachments.map((a) => `[${a.name}]\n${a.text}`).join("\n\n---\n\n");
    try {
      const history = [
        { role: "user", content: `Explain this concept: ${concept}` },
        { role: "assistant", content: result },
        ...followUps.flatMap((fu) => [
          { role: "user", content: fu.question },
          { role: "assistant", content: fu.answer },
        ]),
        { role: "user", content: q || "Please explain the attached file(s)." },
      ];
      const authHeaders2 = await getAuthHeaders();
      const res = await fetch(`${api}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders2 },
        body: JSON.stringify({
          messages: history,
          book_id: book?.id || null,
          images: imageAttachments.map((a) => a.base64),
          doc_context: doc_context || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setFollowUps((prev) => [...prev, {
          question: q || "📎 Files",
          answer: data.reply,
          previews: imageAttachments.map((a) => a.preview),
          docNames: docAttachments.map((a) => a.name),
        }]);
      } else {
        showToast(data.error || "Follow-up failed");
      }
    } catch {
      showToast("Failed to send follow-up");
    }
    setFollowUpLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !loading) doFetch("explain", "normal");
  };
  const handleFollowUpKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !followUpLoading) { e.preventDefault(); sendFollowUp(); }
  };

  // Chip renderer for attachment lists
  const renderChips = (list, setFn) => list.map((a) => (
    <div key={a.id} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: a.loading ? "#f1f5f9" : a.error ? "#fee2e2" : a.type === "image" ? "#f0fdf4" : "#e0e7ff",
      border: `1px solid ${a.loading ? "#e2e8f0" : a.error ? "#fca5a5" : a.type === "image" ? "#86efac" : "#a5b4fc"}`,
      borderRadius: 8, padding: "3px 8px", fontSize: "0.78rem",
      color: a.error ? "#dc2626" : a.type === "image" ? "#166534" : "#3730a3", maxWidth: 180,
    }}>
      {a.type === "image" && a.preview
        ? <img src={a.preview} alt="" style={{ width: 16, height: 16, objectFit: "cover", borderRadius: 3 }} />
        : <span>{a.loading ? "⏳" : a.error ? "⚠️" : "📄"}</span>}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.error ? `${a.name}: ${a.error}` : a.name}</span>
      {!a.loading && (
        <button onClick={() => setFn((prev) => prev.filter((x) => x.id !== a.id))} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "1rem", lineHeight: 1, padding: "0 2px" }}>×</button>
      )}
    </div>
  ));

  return (
    <div className="tab-content">
      {showTemplateModal && (
        <PromptTemplateModal
          modes={["explain"]}
          initialMode="explain"
          onClose={(wasSaved) => {
            setShowTemplateModal(false);
            if (wasSaved) setSavedTemplates(loadTemplates());
          }}
        />
      )}
      <div className="form-section">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <label style={{ marginBottom: 0 }}>{t("explainConceptLabel")}</label>
          <button
            className={`pt-trigger-btn ${savedTemplates.explain ? "has-custom" : ""}`}
            onClick={() => setShowTemplateModal(true)}
            title={t("promptBtnTooltip")}
          >
            {t("promptBtn")}
          </button>
        </div>
        <div className="explain-concept-row">
          <button
            className="icon-btn"
            title="Attach files — images, PDF, Word, Excel, CSV, PowerPoint"
            onClick={() => conceptFileRef.current?.click()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input
            className="concept-input"
            placeholder={t("conceptPlaceholder")}
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            onKeyDown={handleKey}
            onPaste={(e) => {
              const items = e.clipboardData?.items;
              if (!items) return;
              for (const item of items) {
                if (item.type.startsWith("image/")) {
                  e.preventDefault();
                  readAsAttachment(item.getAsFile(), setConceptAttachments);
                  break;
                }
              }
            }}
          />
        </div>
        <input
          type="file"
          accept="image/*,.pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.ppt"
          multiple
          ref={conceptFileRef}
          style={{ display: "none" }}
          onChange={(e) => { Array.from(e.target.files).forEach((f) => readAsAttachment(f, setConceptAttachments)); e.target.value = ""; }}
        />
        {conceptAttachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {renderChips(conceptAttachments, setConceptAttachments)}
          </div>
        )}
      </div>

      <div className="explain-btn-row">
        <button
          className="action-btn primary"
          onClick={() => doFetch("explain", "normal")}
          disabled={!!loading}
        >
          {loading === "normal" ? (
            <div className="loading-dots"><span /><span /><span /></div>
          ) : (
            t("explainBtn")
          )}
        </button>
      </div>

      {result && (
        <>
          <AiResult
            content={result}
            onAddNote={onAddNote ? () => onAddNote(`Explanation: ${concept}`, result) : null}
          />

          {/* Follow-up thread */}
          {followUps.map((fu, i) => (
            <div key={i} className="followup-exchange">
              <div className="followup-question">
                🙋 {fu.question}
                {fu.previews?.map((src, j) => (
                  <img key={j} src={src} alt="sent" style={{ display: "block", maxWidth: "100%", maxHeight: 100, borderRadius: 6, marginTop: 4, objectFit: "contain" }} />
                ))}
                {fu.docNames?.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {fu.docNames.map((n, j) => (
                      <span key={j} style={{ background: "#e0e7ff", borderRadius: 6, padding: "2px 8px", fontSize: "0.78rem", color: "#3730a3" }}>📄 {n}</span>
                    ))}
                  </div>
                )}
              </div>
              <AiResult
                content={fu.answer}
                onAddNote={onAddNote ? () => onAddNote(`Follow-up: ${fu.question}`, fu.answer) : null}
              />
            </div>
          ))}

          <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            <input
              type="file"
              accept="image/*,.pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.ppt"
              multiple
              ref={followFileRef}
              style={{ display: "none" }}
              onChange={(e) => { Array.from(e.target.files).forEach((f) => readAsAttachment(f, setFollowAttachments)); e.target.value = ""; }}
            />
            {followAttachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                {renderChips(followAttachments, setFollowAttachments)}
              </div>
            )}
            <div className="followup-input-row" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
              <button
                className="icon-btn"
                title="Attach files — images, PDF, Word, Excel, CSV, PowerPoint"
                onClick={() => followFileRef.current?.click()}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <textarea
                ref={followUpRef}
                className="followup-input"
                placeholder={t("followUpPlaceholderExplain")}
                value={followUpInput}
                rows={1}
                onChange={(e) => { setFollowUpInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                onKeyDown={handleFollowUpKey}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  for (const item of items) {
                    if (item.type.startsWith("image/")) {
                      e.preventDefault();
                      readAsAttachment(item.getAsFile(), setFollowAttachments);
                      break;
                    }
                  }
                }}
              />
              <button
                className="action-btn primary"
                style={{ width: "auto", padding: "8px 14px", marginTop: 0, flexShrink: 0 }}
                onClick={sendFollowUp}
                disabled={followUpLoading || (!followUpInput.trim() && !followAttachments.some((a) => !a.loading && !a.error))}
              >
                {followUpLoading ? <div className="loading-dots"><span /><span /><span /></div> : "↩ Ask"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
